import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Send, X, Info } from "lucide-react";
import { cap, DEFAULT_PERSONA } from "./persona";
import { API } from "../../App";
import { STATUS_TONE } from "./portalStatus";

const BLANK = {
  first_name: "", last_name: "", referral_email: "", referral_phone: "", department: "",
  move_date: "", preferred_neighborhood: "", bedrooms: "", budget: "", apartment_preferences: "", consent: false,
};

const inputCls = "w-full px-3.5 py-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 " +
  "placeholder:text-slate-400 focus:outline-none focus:border-[#C8A96B] focus:ring-2 focus:ring-[#C8A96B]/20";

const Field = ({ label, id, children }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
    {children}
  </div>
);

export const PortalReferralForm = ({ token, persona = DEFAULT_PERSONA, onSubmitted, onClose, onViewReferral }) => {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) { setError(`${cap(persona.label)} first name is required.`); return; }
    if (!form.referral_email && !form.referral_phone) { setError("Please add an email or phone so we can reach them."); return; }
    setSaving(true); setError(null);
    const payload = {
      referral_name: `${form.first_name} ${form.last_name}`.trim(),
      referral_email: form.referral_email || undefined,
      referral_phone: form.referral_phone || undefined,
      department: form.department || undefined,
      move_date: form.move_date || undefined,
      preferred_neighborhood: form.preferred_neighborhood || undefined,
      bedrooms: form.bedrooms || undefined,
      budget: form.budget ? parseFloat(form.budget) : undefined,
      apartment_preferences: form.apartment_preferences || undefined,
      consent: form.consent,
    };
    try {
      const { data } = await axios.post(`${API}/portal/${token}/referrals`, payload);
      setResult(data);
      onSubmitted();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : d?.message || "Submission failed — please try again.");
    } finally { setSaving(false); }
  };

  if (result) {
    const r = result.referral;
    return (
      <div className="p-6 md:p-8 text-center" data-testid="portal-referral-success">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-[#0A2342]">
          {result.duplicate ? "Already on file ✓" : "Referral Submitted ✓"}
        </h3>
        {result.duplicate && (
          <p className="mt-1 text-sm text-slate-600 inline-flex items-center gap-1" data-testid="portal-referral-duplicate">
            <Info className="w-4 h-4" /> We already have an open referral for this {persona.label} — no duplicate was created.
          </p>
        )}
        <dl className="mt-5 inline-grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-left text-sm">
          <dt className="text-slate-500">Referral ID</dt><dd className="font-mono text-slate-800" data-testid="portal-success-id">{r.id.slice(0, 8).toUpperCase()}</dd>
          <dt className="text-slate-500">{cap(persona.label)}</dt><dd className="font-semibold text-slate-900" data-testid="portal-success-name">{r.referral_name}</dd>
          <dt className="text-slate-500">Status</dt>
          <dd><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_TONE[r.status] || ""}`} data-testid="portal-success-status">{r.status_label}</span></dd>
        </dl>
        <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
          <button onClick={() => onViewReferral(r.id)} data-testid="portal-success-view-btn"
            className="px-5 py-3 rounded-xl bg-[#0A2342] text-white font-semibold text-sm hover:bg-[#1F2A44]">View Referral</button>
          <button onClick={() => { setResult(null); setForm(BLANK); }} data-testid="portal-success-another-btn"
            className="px-5 py-3 rounded-xl border border-slate-300 text-[#0A2342] font-semibold text-sm hover:bg-slate-50">{persona.cta_again}</button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="p-5 md:p-7" data-testid="portal-referral-form">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h3 className="text-lg font-bold text-[#0A2342]">{persona.cta}</h3>
          <p className="text-xs text-slate-500 mt-0.5">Only a name plus an email or phone is required — the rest helps us move faster.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close form" className="p-2 rounded-lg hover:bg-slate-100" data-testid="portal-form-close">
          <X className="w-5 h-5 text-slate-500" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={`${cap(persona.label)} first name *`} id="pf-first"><input id="pf-first" className={inputCls} value={form.first_name} onChange={set("first_name")} required data-testid="portal-form-first-name" /></Field>
        <Field label={`${cap(persona.label)} last name`} id="pf-last"><input id="pf-last" className={inputCls} value={form.last_name} onChange={set("last_name")} data-testid="portal-form-last-name" /></Field>
        <Field label="Email" id="pf-email"><input id="pf-email" type="email" className={inputCls} value={form.referral_email} onChange={set("referral_email")} data-testid="portal-form-email" /></Field>
        <Field label="Phone" id="pf-phone"><input id="pf-phone" type="tel" className={inputCls} value={form.referral_phone} onChange={set("referral_phone")} data-testid="portal-form-phone" /></Field>
        <Field label="Department / team" id="pf-dept"><input id="pf-dept" className={inputCls} value={form.department} onChange={set("department")} placeholder="e.g. Engineering" data-testid="portal-form-department" /></Field>
        <Field label="Expected move date" id="pf-move"><input id="pf-move" type="date" className={inputCls} value={form.move_date} onChange={set("move_date")} data-testid="portal-form-move-date" /></Field>
        <Field label="Preferred Houston area" id="pf-area"><input id="pf-area" className={inputCls} value={form.preferred_neighborhood} onChange={set("preferred_neighborhood")} placeholder="Energy Corridor, Medical Center…" data-testid="portal-form-area" /></Field>
        <Field label="Bedrooms" id="pf-beds">
          <select id="pf-beds" className={inputCls} value={form.bedrooms} onChange={set("bedrooms")} data-testid="portal-form-bedrooms">
            <option value="">Not sure yet</option><option>Studio</option><option>1BR</option><option>2BR</option><option>3BR+</option>
          </select>
        </Field>
        <Field label="Estimated budget ($/mo)" id="pf-budget"><input id="pf-budget" type="number" min="0" className={inputCls} value={form.budget} onChange={set("budget")} placeholder="2000" data-testid="portal-form-budget" /></Field>
        <div className="sm:col-span-2">
          <Field label="Notes" id="pf-notes"><textarea id="pf-notes" rows={2} className={inputCls} value={form.apartment_preferences} onChange={set("apartment_preferences")} placeholder="Pets, commute, must-haves…" data-testid="portal-form-notes" /></Field>
        </div>
        <label className="sm:col-span-2 flex items-start gap-2.5 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 w-4 h-4 accent-[#0A2342]" data-testid="portal-form-consent" />
          <span>This {persona.label} knows I'm sharing their information and is OK with Urban Nest Quest reaching out (optional).</span>
        </label>
      </div>
      {error && <p className="mt-3 text-sm text-red-600" role="alert" data-testid="portal-form-error">{error}</p>}
      <button type="submit" disabled={saving} data-testid="portal-form-submit"
        className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#C8A96B] hover:bg-[#B5915A] text-[#0A2342] font-bold disabled:opacity-60">
        {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <><Send className="w-4 h-4" /> Submit Referral</>}
      </button>
    </form>
  );
};
